// PrizeWheel.jsx
import React, { useState, useEffect } from "react";
import { gameAPI } from "../services/api";
import "./PrizeWheel.css";

const PrizeWheel = ({ sessionToken, prizes, onPrizeWon }) => {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [wonPrize, setWonPrize] = useState(null);

  // Colors optimized for 4 segments
  const segmentColors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4"];

  // Log all prizes on component mount
  useEffect(() => {
    console.log("=== PRIZE WHEEL INITIALIZED ===");
    console.log("Total prizes:", prizes.length);
    console.log(
      "All prizes:",
      prizes.map((p, i) => ({
        index: i,
        id: p.id,
        name: p.name,
        type: p.prize_type,
        segmentStart: i * (360 / prizes.length),
        segmentEnd: (i + 1) * (360 / prizes.length),
      }))
    );
    console.log("===============================");
  }, [prizes]);

  const spinWheel = async () => {
    if (spinning) return;

    setSpinning(true);
    console.log("\n=== SPIN STARTED ===");

    try {
      const response = await gameAPI.spinWheel(sessionToken);
      const { prize, claim_code } = response.data;

      console.log("Backend response:", response.data);
      console.log("Prize from backend:", {
        id: prize.id,
        name: prize.name,
        type: prize.prize_type,
        claim_code: claim_code,
      });

      const prizeIndex = prizes.findIndex((p) => p.id === prize.id);

      console.log("Prize index in array:", prizeIndex);

      if (prizeIndex === -1) {
        console.error("❌ ERROR: Won prize not found in prizes array");
        console.error("Looking for prize ID:", prize.id);
        console.error(
          "Available prize IDs:",
          prizes.map((p) => p.id)
        );
        setSpinning(false);
        return;
      }

      const segmentAngle = 360 / prizes.length;
      const currentRotation = rotation % 360;

      // The pointer is at the top (0°/360°)
      // We need to rotate so the CENTER of the winning segment is at the top
      // Segment centers are at: index * segmentAngle + segmentAngle/2
      const segmentCenter = prizeIndex * segmentAngle + segmentAngle / 2;

      // Since we're rotating the wheel and the pointer is fixed at top,
      // we need to rotate TO that segment's center
      // The wheel rotates clockwise, so we rotate by the segment center angle
      const targetPosition = segmentCenter;

      // Add full spins
      const fullSpins = 5 * 360;
      const finalRotation = currentRotation + fullSpins + targetPosition;

      console.log("Rotation calculation:");
      console.log("  - Segment angle:", segmentAngle);
      console.log("  - Current rotation:", currentRotation);
      console.log("  - Segment center:", segmentCenter);
      console.log("  - Target position:", targetPosition);
      console.log("  - Full spins:", fullSpins);
      console.log("  - Final rotation:", finalRotation);

      console.log("Where wheel will land:");
      console.log("  - Prize index:", prizeIndex);
      console.log("  - Prize name:", prizes[prizeIndex].name);
      console.log("  - Segment start angle:", prizeIndex * segmentAngle);
      console.log("  - Segment center angle:", segmentCenter);
      console.log("  - Segment end angle:", (prizeIndex + 1) * segmentAngle);

      setRotation(finalRotation);

      setTimeout(() => {
        const normalizedPosition = finalRotation % 360;
        console.log("\n=== SPIN COMPLETE ===");
        console.log("Final position (normalized):", normalizedPosition);
        console.log("Expected segment:", prizeIndex);
        console.log(
          "Segment range:",
          `${prizeIndex * segmentAngle}° - ${(prizeIndex + 1) * segmentAngle}°`
        );
        console.log(
          "Is in correct segment?",
          normalizedPosition >= prizeIndex * segmentAngle &&
            normalizedPosition < (prizeIndex + 1) * segmentAngle
        );
        console.log("Won prize:", prize.name);
        console.log("Claim code:", claim_code);
        console.log("====================\n");

        setWonPrize({ ...prize, claim_code });
        setSpinning(false);
        onPrizeWon({ ...prize, claim_code });
      }, 4000);
    } catch (error) {
      console.error("❌ ERROR spinning wheel:", error);
      console.error("Error details:", error.response?.data || error.message);
      setSpinning(false);
    }
  };

  return (
    <div className="prize-wheel-container">
      <h2>🎉 Spin the Wheel!</h2>
      <p className="wheel-subtitle">
        You've earned a chance to win an amazing prize!
      </p>

      <div className="wheel-wrapper">
        <div className="wheel-pointer">▼</div>

        <div
          className="wheel"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: spinning
              ? "transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)"
              : "none",
          }}
        >
          <div className="wheel-inner">
            {prizes.map((prize, index) => {
              const segmentAngle = 360 / prizes.length;
              const startAngle = index * segmentAngle;

              return (
                <div
                  key={prize.id}
                  className="wheel-segment"
                  style={{
                    transform: `rotate(${startAngle}deg)`,
                    background: segmentColors[index % segmentColors.length],
                  }}
                >
                  <div className="segment-content">
                    <span className="segment-text">{prize.name}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="wheel-center"></div>
        </div>
      </div>

      {!wonPrize && (
        <button className="spin-button" onClick={spinWheel} disabled={spinning}>
          {spinning ? "🎰 Spinning..." : "🎰 SPIN NOW!"}
        </button>
      )}

      {wonPrize && (
        <div className="prize-result">
          <h3>🎊 Congratulations!</h3>
          <div className="won-prize">
            <h4>{wonPrize.name}</h4>
            <p>{wonPrize.description}</p>
            {wonPrize.prize_type !== "no_prize" && (
              <div className="claim-code">
                <strong>Your Claim Code:</strong>
                <code>{wonPrize.claim_code}</code>
                <p className="claim-instructions">
                  Save this code! Use it to claim your prize at the redemption
                  center.
                </p>
              </div>
            )}
            {wonPrize.prize_type === "no_prize" && (
              <p className="try-again-message">
                Thanks for playing! Better luck next time! 🍀
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrizeWheel;
